import { ApplicationView, useApplicationView } from "@hooks/useApplicationView";
import { useMemo } from "react";
import { useEntities } from "@hooks/useEntities";
import { useBillingType } from "@hooks/useBillingType";
import { SubscriptionType } from "@common/types/billing";
import { useCanManageWorkspaceBilling } from "@hooks/permissions/useCanManageWorkspaceBilling";
import { useCanViewBillingTransfer } from "@hooks/permissions/useCanViewBillingTransfer";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { useCanManageWorkspace } from "@hooks/permissions/useCanManageWorkspace";
import { getEntityPaneKey } from "./lib";
import {
    EntitySettingsPages,
    OrganizationSettingsPages,
    UserSettingsPages,
    WorkspaceSettingsPages,
} from "./constants";

interface BuildMainViewPagesParams {
    userSettingsPages: string[];
    entities: ReturnType<typeof useEntities>;
    canManageWorkspaceMembers: boolean;
    canManageBilling: boolean;
    canViewBillingTransfer: boolean;
    showPlans: boolean;
    canManageWorkspace: boolean;
}

function buildMainViewPages(params: BuildMainViewPagesParams): string[] {
    const pages: string[] = [...params.userSettingsPages];

    pages.push(
        ...params.entities
            .map((e) =>
                Object.values(EntitySettingsPages).map((p) =>
                    getEntityPaneKey(p, e.id),
                ),
            )
            .flat(),
    );

    pages.push(WorkspaceSettingsPages.DOCUMENT_INBOX);
    pages.push(WorkspaceSettingsPages.INTEGRATIONS);

    if (params.canManageWorkspace) {
        pages.push(WorkspaceSettingsPages.SENDING_DOMAINS);
    }

    if (params.canManageWorkspaceMembers) {
        pages.push(WorkspaceSettingsPages.MEMBERS);
    }

    pages.push(WorkspaceSettingsPages.TASK_GROUPS);
    pages.push(WorkspaceSettingsPages.LOCKS);

    if (params.showPlans) {
        pages.push(WorkspaceSettingsPages.PLANS);
    }
    if (params.canManageBilling) {
        pages.push(WorkspaceSettingsPages.BILLING);
    }
    if (params.canViewBillingTransfer) {
        pages.push(WorkspaceSettingsPages.BILLING_TRANSFER);
    }
    pages.push(WorkspaceSettingsPages.ACCOUNTING);

    return pages;
}

export function useValidSettingsPages(): string[] {
    const { closeManagement, personalAccessTokens: patsFlagEnabled } =
        useTypedFlags();
    const applicationView = useApplicationView();
    const entities = useEntities();

    const canManageWorkspaceMembers = useCanPerformWorkspaceAction(
        WorkspaceAction.MEMBERS_EDIT,
    );
    const canManageBilling = useCanManageWorkspaceBilling();
    const canViewBillingTransfer = useCanViewBillingTransfer();
    const canManageOrganizationSettings = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_ORGANIZATION_SETTINGS,
    );
    const canManageWorkspace = useCanManageWorkspace();
    const billingType = useBillingType();

    const showPlans =
        canManageBilling && billingType === SubscriptionType.FLAT_RATE;

    const userSettingsPages = useMemo(() => {
        const allPages = Object.values(UserSettingsPages);
        if (patsFlagEnabled) {
            return allPages;
        }
        return allPages.filter((page) => page !== UserSettingsPages.MCP_CLI);
    }, [patsFlagEnabled]);

    return useMemo(() => {
        switch (applicationView) {
            case ApplicationView.PERSONAL_VIEW:
                return [...userSettingsPages];
            case ApplicationView.ORGANIZATION_VIEW: {
                const organizationViewSettingsPages: Array<
                    UserSettingsPages | OrganizationSettingsPages
                > = [...userSettingsPages];

                if (canManageOrganizationSettings) {
                    organizationViewSettingsPages.push(
                        OrganizationSettingsPages.FIRM_INFORMATION,
                        OrganizationSettingsPages.TASK_GROUPS,
                    );

                    if (closeManagement) {
                        organizationViewSettingsPages.push(
                            OrganizationSettingsPages.CLOSES,
                        );
                    }
                }

                return organizationViewSettingsPages;
            }
            case ApplicationView.MAIN:
                return buildMainViewPages({
                    userSettingsPages,
                    entities,
                    canManageWorkspaceMembers,
                    canManageBilling,
                    canViewBillingTransfer,
                    showPlans,
                    canManageWorkspace,
                });
            default:
                return [];
        }
    }, [
        applicationView,
        entities,
        canManageWorkspaceMembers,
        canManageBilling,
        showPlans,
        canViewBillingTransfer,
        canManageOrganizationSettings,
        closeManagement,
        userSettingsPages,
        canManageWorkspace,
    ]);
}
